/**
 * Utilidad de optimización de imágenes.
 * Convierte cualquier imagen (png, jpeg, jpg, webp) a WebP usando canvas
 * para reducir el tamaño de archivo antes del upload.
 *
 * Flujo:
 *   PNG / JPG / WebP  →  canvas (redimensionado si excede MAX_DIMENSION_PX)  →  WebP @ 0.65
 */

/** Calidad WebP aplicada al exportar (0–1). 0.65 = buena calidad visual, reducción agresiva de peso. */
const WEBP_QUALITY = 0.65;

/** Dimensión máxima (ancho o alto) en píxeles. Imágenes mayores se escalan manteniendo proporción. */
const MAX_DIMENSION_PX = 1920;

const DEFAULT_MAX_SIZE_MB = 5;

export interface ImageOptimizationResult {
  file: File;
  originalSize: number;
  optimizedSize: number;
  reductionPercent: number;
}

/**
 * Optimiza un archivo de imagen convirtiéndolo a WebP mediante canvas.
 *
 * Flujo:
 * 1. Crea un <img> en memoria y carga la imagen desde el File via Object URL
 * 2. Si alguna dimensión supera MAX_DIMENSION_PX, escala proporcionalmente
 * 3. Dibuja la imagen en un <canvas> con las dimensiones resultantes
 * 4. Exporta el canvas como `image/webp` con calidad 0.65
 *
 * @param file - Archivo de imagen original (webp, png, jpeg, jpg)
 * @returns Nuevo File en formato WebP con el mismo nombre base
 */
export const optimizeImageToWebP = (
  file: File
): Promise<ImageOptimizationResult> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      // Calcular dimensiones finales respetando el ratio
      let { naturalWidth: w, naturalHeight: h } = img;
      if (w > MAX_DIMENSION_PX || h > MAX_DIMENSION_PX) {
        const ratio = Math.min(MAX_DIMENSION_PX / w, MAX_DIMENSION_PX / h);
        w = Math.round(w * ratio);
        h = Math.round(h * ratio);
      }

      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(
          new Error(
            '[ImageHelper] No se pudo obtener el contexto 2D del canvas'
          )
        );
        return;
      }

      ctx.drawImage(img, 0, 0, w, h);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('[ImageHelper] canvas.toBlob devolvió null'));
            return;
          }

          const baseName = file.name.replace(/\.[^.]+$/, '');
          const optimizedFile = new File([blob], `${baseName}.webp`, {
            type: 'image/webp',
          });

          const originalKB = (file.size / 1024).toFixed(1);
          const optimizedKB = (optimizedFile.size / 1024).toFixed(1);
          const saving = ((1 - optimizedFile.size / file.size) * 100).toFixed(
            1
          );

          console.log(
            `[ImageHelper] "${file.name}" → original: ${originalKB} KB | optimizado: ${optimizedKB} KB | ahorro: ${saving}%`
          );

          resolve({
            file: optimizedFile,
            originalSize: file.size,
            optimizedSize: optimizedFile.size,
            reductionPercent: parseFloat(saving),
          });
        },
        'image/webp',
        WEBP_QUALITY
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(
        new Error('[ImageHelper] No se pudo cargar la imagen para optimizar')
      );
    };

    img.src = objectUrl;
  });
};

/**
 * Valida que el tamaño en bytes no supere el límite permitido.
 *
 * @param sizeBytes - Tamaño del archivo en bytes.
 * @param maxMB - Límite máximo en megabytes. Por defecto 5 MB.
 */
export const isImageSizeValid = (
  sizeBytes: number,
  maxMB: number = DEFAULT_MAX_SIZE_MB
): boolean => sizeBytes / 1024 / 1024 <= maxMB;

export const ImageHelper = {
  optimizeImageToWebP,
  isImageSizeValid,
};
